import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  clampSidePanelWidth,
  DEFAULT_SIDE_PANEL_WIDTH,
  getMaxSidePanelWidth,
  isSidePanelResizable,
  MIN_SIDE_PANEL_WIDTH,
  SIDE_PANEL_WIDTH_STEP,
} from 'components/customer/lib/side_panel_width';
import SetSidePanelWidth from 'actions/agent_preferences/set_side_panel_width';
import { useExecuteAction } from 'components/hooks/connect_hooks';

/**
 * The handle sits on the panel's left edge, so dragging left widens it.
 *
 * @param {boolean} args.isEnabled false leaves the panel at its unresizable width
 * @param {number} args.storedWidth agentPreferences.sidePanelWidth; 0 when never dragged
 * @param {object} args.wrapperRef ref to the panel wrapper, whose parent is the customer row
 */
export default function useSidePanelResize({ isEnabled, storedWidth, wrapperRef }) {
  const executeAction = useExecuteAction();
  const containerWidth = useContainerWidth(wrapperRef, isEnabled);
  const [pendingWidth, setPendingWidth] = useState(null);
  const [isResizing, setIsResizing] = useState(false);

  const width = clampSidePanelWidth(pendingWidth === null ? storedWidth : pendingWidth, containerWidth);
  const maxWidth = getMaxSidePanelWidth(containerWidth);

  // Read from document listeners, which are bound once and would otherwise
  // close over the values as they were at pointerdown.
  const widthRef = useRef(width);
  const containerWidthRef = useRef(containerWidth);
  const storedWidthRef = useRef(storedWidth);
  useLayoutEffect(() => {
    widthRef.current = width;
    containerWidthRef.current = containerWidth;
    storedWidthRef.current = storedWidth;
  }, [containerWidth, storedWidth, width]);

  const persistWidth = useCallback(
    nextWidth => executeAction(SetSidePanelWidth, { width: nextWidth }),
    [executeAction]
  );

  // For widths that reached us through the clamp — every drag and key press. The
  // stored width is unclamped so it survives moving between screens, which only
  // holds if this refuses a width the clamp produced rather than the agent: on a
  // narrow window it is already pinned to the ceiling, and writing it back would
  // overwrite a wider choice made on a bigger screen. Resetting is exempt
  // because the default is stated outright, not inferred from a gesture.
  const commit = useCallback(
    nextWidth => {
      const stored = storedWidthRef.current;
      if (stored && stored !== nextWidth && clampSidePanelWidth(stored, containerWidthRef.current) === nextWidth) {
        return;
      }
      persistWidth(nextWidth);
    },
    [persistWidth]
  );

  // Hold the dragged width until the store reports it back: clearing it when the
  // drag ends falls back to storedWidth for the render or two before the write
  // propagates, which snaps the panel to its pre-drag size.
  useEffect(() => {
    if (pendingWidth !== null && pendingWidth === storedWidth) {
      setPendingWidth(null);
    }
  }, [pendingWidth, storedWidth]);

  const resize = useCallback(nextWidth => {
    setPendingWidth(clampSidePanelWidth(nextWidth, containerWidthRef.current));
  }, []);

  // Abandons a drag rather than finishing it, so closing the panel mid-drag
  // neither persists a half-finished width nor touches state on a gone
  // component.
  const cancelDragRef = useRef(null);
  useEffect(() => () => cancelDragRef.current && cancelDragRef.current(), []);

  const onHandlePointerDown = useCallback(
    event => {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }
      event.preventDefault();

      const handle = event.currentTarget;
      const { pointerId } = event;
      const startX = event.clientX;
      const startWidth = widthRef.current;
      let latestWidth = startWidth;
      let didResize = false;

      const onPointerMove = moveEvent => {
        if (moveEvent.pointerId !== pointerId) {
          return;
        }
        const next = clampSidePanelWidth(startWidth + (startX - moveEvent.clientX), containerWidthRef.current);
        if (next === latestWidth) {
          return;
        }
        latestWidth = next;
        didResize = true;
        setPendingWidth(next);
      };

      const teardown = () => {
        cancelDragRef.current = null;
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', finishDrag);
        document.removeEventListener('pointercancel', finishDrag);
        restoreBodyDuringDrag();
        if (handle.hasPointerCapture && handle.hasPointerCapture(pointerId)) {
          handle.releasePointerCapture(pointerId);
        }
      };

      // Capture does not stop a second pointer's events reaching the document,
      // so every handler ignores anything but the pointer that started the drag.
      function finishDrag(endEvent) {
        if (endEvent.pointerId !== pointerId) {
          return;
        }
        teardown();
        setIsResizing(false);
        // A click that never moved is not a resize; committing it would write a
        // clamped width back over a wider stored one.
        if (didResize) {
          commit(latestWidth);
        }
      }

      cancelDragRef.current = teardown;
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', finishDrag);
      document.addEventListener('pointercancel', finishDrag);
      // Keeps the drag alive when the pointer leaves the window; a plain document
      // mouseup lands elsewhere and the drag would resume on re-entry.
      if (handle.setPointerCapture) {
        handle.setPointerCapture(pointerId);
      }
      suppressBodyDuringDrag();
      setIsResizing(true);
    },
    [commit]
  );

  const onHandleKeyDown = useCallback(
    event => {
      const target = keyboardTarget(event.key, widthRef.current, containerWidthRef.current);
      if (target === null) {
        return;
      }
      event.preventDefault();
      resize(target);
    },
    [resize]
  );

  // Arrow keys repeat while held, so write once on release rather than per key.
  const onHandleKeyUp = useCallback(
    event => {
      if (keyboardTarget(event.key, widthRef.current, containerWidthRef.current) !== null) {
        commit(widthRef.current);
      }
    },
    [commit]
  );

  const onHandleDoubleClick = useCallback(() => persistWidth(DEFAULT_SIDE_PANEL_WIDTH), [persistWidth]);

  return {
    isResizable: isEnabled && isSidePanelResizable(containerWidth),
    isResizing,
    maxWidth,
    onHandleDoubleClick,
    onHandleKeyDown,
    onHandleKeyUp,
    onHandlePointerDown,
    width,
  };
}

/** Width the key should move to, or null when we don't handle it. */
function keyboardTarget(key, currentWidth, containerWidth) {
  switch (key) {
    case 'ArrowLeft':
      return currentWidth + SIDE_PANEL_WIDTH_STEP;
    case 'ArrowRight':
      return currentWidth - SIDE_PANEL_WIDTH_STEP;
    case 'Home':
      return MIN_SIDE_PANEL_WIDTH;
    case 'End':
      return getMaxSidePanelWidth(containerWidth);
    default:
      return null;
  }
}

/**
 * Measured in a layout effect so the first painted frame has a real number: at 0
 * the clamp falls back to MIN/MAX and can allow a width the window can't hold.
 */
function useContainerWidth(wrapperRef, isEnabled) {
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const container = isEnabled && wrapperRef.current && wrapperRef.current.parentElement;
    if (!container) {
      return undefined;
    }

    const measure = () => setContainerWidth(container.getBoundingClientRect().width);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      // Coarser than watching the row, but keeps the width off 0, where the clamp
      // and the handle's visibility check stop reflecting the window.
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isEnabled, wrapperRef]);

  return containerWidth;
}

// Holds the resize cursor while the pointer is off the handle, and stops the
// drag selecting conversation text.
function suppressBodyDuringDrag() {
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
}

function restoreBodyDuringDrag() {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}
